import { useCallback, useMemo } from 'react';
import { useRoute } from '@huaca/ui';
import { CATEGORIES, EMPLOYMENT_TYPES, PROFESSIONS, PUBLISHER_TYPES, REGIONS, TENDER_TYPES } from './taxonomy';
import { CLOSING_WINDOWS, NATURAL_DIRECTION, SORT_DIRECTIONS } from './types';
import type { Category, ClosingWindow, EmploymentType, Kind, PostingStatus, Profession, PublisherType, Region, Sort, SortDirection, TenderType } from './types';

/**
 * Search state lives in the URL, not in component memory.
 *
 * The deciding case is mundane: browse to page 4, open a posting, press Back. Opening the detail is
 * a navigation, so the list unmounts — and if the URL carried no state, Back lands the user on page
 * 1 of an unfiltered search, every single time. That is the most common interaction in the product.
 * Putting the state in the URL also makes a search shareable ("open construction tenders, north"),
 * survivable across a refresh, and undoable with the browser's own Back button.
 *
 * The page number is in here too, so a bookmark reopens the page it was taken on and Back undoes
 * one page turn rather than skipping every turn since the last filter change.
 */

export interface SearchQuery {
  q: string;
  /**
   * The seven taxonomy dimensions are MULTI-SELECT: an array of chosen values, or absent.
   *
   * `undefined` and `[]` must never both be reachable, or two spellings mean the same thing and the
   * URL stops being canonical. `parseSearchQuery` collapses an empty selection to `undefined`, so
   * absent is the only way to say "no filter on this dimension".
   */
  kind?: Kind[];
  category?: Category[];
  region?: Region[];
  tenderType?: TenderType[];
  employmentType?: EmploymentType[];
  profession?: Profession[];
  publisherType?: PublisherType[];
  /** Deadline windows. Multi-select like the rest — see `CLOSING_WINDOWS`. */
  closing?: ClosingWindow[];
  /** NOT multi-select: a yes/no, where "either" is what leaving it off already means. */
  securityClearanceRequired?: 'true' | 'false';
  closingFrom?: string;
  closingTo?: string;
  status: PostingStatus;
  /**
   * The sort the user EXPLICITLY chose, or `null` for "whatever suits the query".
   *
   * Nullable rather than pre-resolved because the default is contextual: a text query should come
   * back by relevance, browsing should come back by nearest deadline. Storing the resolved value
   * would pin it — typing into an unsorted list would carry `closing` forward and write it into the
   * URL, silently converting a default into a choice the user never made. `effectiveSort` resolves
   * it at the point of use instead.
   */
  sort: Sort | null;
  /**
   * Sort DIRECTION, or `null` for "whatever suits the key".
   *
   * Nullable for the same reason `sort` is: each key has a natural direction, and storing the
   * resolved value would pin it — switching sort key would carry the previous key's direction into
   * the URL as though the reader had chosen it.
   */
  dir: SortDirection | null;
  /**
   * 1-BASED page number.
   *
   * This replaced an opaque keyset cursor, which also meant deleting a module-level map of
   * cursor → previous-cursor: a keyset cannot compute the page before its anchor, so the pages
   * already visited had to be remembered, and that memory died whenever the reader opened a posting
   * and came back. A page number is arithmetic — "previous" is `page - 1`, always, even on a link
   * someone else sent.
   */
  page: number;
}

/** Defaults are OMITTED from the URL, so an empty query string means exactly "the default view". */
const DEFAULTS = { q: '', status: 'open' as PostingStatus };

const SORTS: readonly string[] = ['relevance', 'closing', 'newest'];

/**
 * The sort that will actually be applied: the user's choice, or the one that fits the query.
 *
 * Ranking is the point of a text query, and a deadline order is the point of browsing. The server
 * resolves the identical rule; this mirrors it so the control can show the truth before the first
 * response arrives, rather than displaying one order while another is being fetched.
 */
export function effectiveSort(query: Pick<SearchQuery, 'q' | 'sort'>): Sort {
  return query.sort ?? (query.q.trim() ? 'relevance' : 'closing');
}

/**
 * The direction that will actually be applied — the reader's choice, or the key's natural one.
 *
 * The server resolves the identical rule. Mirrored here so a table header can mark itself correctly
 * on first paint instead of after the response.
 */
export function effectiveDirection(query: Pick<SearchQuery, 'q' | 'sort' | 'dir'>): SortDirection {
  if (query.dir !== null) return query.dir;
  const sort = effectiveSort(query);
  // `relevance` has no direction of its own; it ranks by score. It borrows the browse default so
  // callers never have to special-case it.
  return NATURAL_DIRECTION[sort === 'relevance' ? 'closing' : sort];
}

/**
 * `relevance` needs something to rank. The server rejects the pair with a 400, so clearing the
 * search box while "לפי התאמה" is selected would otherwise turn the whole screen into an error —
 * dropping back to the contextual default is the only reading that is not a dead end.
 */
function normalise(query: SearchQuery): SearchQuery {
  return query.sort === 'relevance' && !query.q.trim() ? { ...query, sort: null } : query;
}

/** The multi-select dimensions, each a closed taxonomy. */
const MULTI = {
  kind: ['tender', 'job'] as readonly string[],
  category: CATEGORIES,
  region: REGIONS,
  tenderType: TENDER_TYPES,
  employmentType: EMPLOYMENT_TYPES,
  profession: PROFESSIONS,
  publisherType: PUBLISHER_TYPES,
  closing: CLOSING_WINDOWS,
} as const;

type MultiKey = keyof typeof MULTI;

const CLEARANCE: readonly string[] = ['true', 'false'];

/**
 * Every selected value for one dimension, from a repeated parameter.
 *
 * A URL is user-editable and shareable, so each value is checked against the taxonomy rather than
 * trusted, and unknown ones are dropped individually — `?region=north&region=atlantis` narrows to
 * the north rather than 400-ing on the whole request.
 *
 * De-duplicated and SORTED, matching the server's own normalisation. Two spellings of one selection
 * would be two URLs for one search, and the facet counts are refetched whenever the serialised
 * filters change — so an unsorted array costs a request and a quota unit per click.
 *
 * `undefined` rather than `[]` when nothing survives — see the note on `SearchQuery`.
 */
function readEnums<K extends MultiKey>(params: URLSearchParams, key: K): string[] | undefined {
  const allowed = MULTI[key] as readonly string[];
  const values = [...new Set(params.getAll(key).filter((v) => allowed.includes(v)))].sort();
  return values.length ? values : undefined;
}

/** The single-valued enum still in the query. */
function readEnum(params: URLSearchParams, key: string, allowed: readonly string[]): string | undefined {
  const value = params.get(key);
  if (value === null) return undefined;
  return allowed.includes(value) ? value : undefined;
}

/** 1-based, defaulting to the first page. Anything unreadable degrades rather than throwing. */
function readPage(params: URLSearchParams): number {
  const raw = Number(params.get('page'));
  return Number.isInteger(raw) && raw >= 1 ? raw : 1;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function readDay(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key);
  if (value === null || !ISO_DAY.test(value)) return undefined;
  return Number.isNaN(Date.parse(value)) ? undefined : value;
}

/**
 * Parse a query string into search state.
 *
 * Anything unrecognised is DROPPED rather than passed through: a hand-edited or truncated URL should
 * degrade to a valid, narrower search instead of producing a 400 the user cannot act on. The server
 * validates independently — this is about not sending it garbage in the first place.
 */
export function parseSearchQuery(search: string): SearchQuery {
  const params = new URLSearchParams(search);
  const status = params.get('status');
  const sort = params.get('sort');

  return normalise({
    q: params.get('q')?.slice(0, 200) ?? DEFAULTS.q,
    kind: readEnums(params, 'kind') as Kind[] | undefined,
    category: readEnums(params, 'category') as Category[] | undefined,
    region: readEnums(params, 'region') as Region[] | undefined,
    tenderType: readEnums(params, 'tenderType') as TenderType[] | undefined,
    employmentType: readEnums(params, 'employmentType') as EmploymentType[] | undefined,
    profession: readEnums(params, 'profession') as Profession[] | undefined,
    publisherType: readEnums(params, 'publisherType') as PublisherType[] | undefined,
    closing: readEnums(params, 'closing') as ClosingWindow[] | undefined,
    securityClearanceRequired: readEnum(params, 'securityClearanceRequired', CLEARANCE) as
      | 'true'
      | 'false'
      | undefined,
    closingFrom: readDay(params, 'closingFrom'),
    closingTo: readDay(params, 'closingTo'),
    status: status === 'closed' || status === 'delisted' ? status : DEFAULTS.status,
    sort: sort !== null && SORTS.includes(sort) ? (sort as Sort) : null,
    dir: readEnum(params, 'dir', SORT_DIRECTIONS) as SortDirection | undefined ?? null,
    // A hand-edited `?page=abc` or `?page=0` degrades to the first page rather than 400-ing.
    page: readPage(params),
  });
}

/**
 * Serialise back to a query string, omitting defaults and empty values.
 *
 * A multi-select dimension is written as a REPEATED parameter (`?category=x&category=y`) rather than
 * one delimited value, because that is what Express parses back into an array without the server
 * having to know a delimiter — and a taxonomy value containing the delimiter would otherwise split
 * into two filters that match nothing.
 *
 * `append`, not `set`: `set` replaces every existing occurrence of the key, so the loop would leave
 * only the last value of each dimension.
 */
export function toSearchParams(query: SearchQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.q !== DEFAULTS.q) params.set('q', query.q);
  for (const key of Object.keys(MULTI) as MultiKey[]) {
    for (const value of query[key] ?? []) params.append(key, value);
  }
  if (query.securityClearanceRequired !== undefined) {
    params.set('securityClearanceRequired', query.securityClearanceRequired);
  }
  if (query.closingFrom) params.set('closingFrom', query.closingFrom);
  if (query.closingTo) params.set('closingTo', query.closingTo);
  if (query.status !== DEFAULTS.status) params.set('status', query.status);
  // Only an explicit choice is written. An absent `sort` is what keeps the default contextual: the
  // same URL means "by relevance" with a query in it and "closing soonest" without one.
  if (query.sort !== null) params.set('sort', query.sort);
  if (query.dir !== null) params.set('dir', query.dir);
  // Page 1 is the default, so it stays out of the URL — an unpaged search and page 1 are one thing.
  if (query.page > 1) params.set('page', String(query.page));
  return params;
}

/**
 * A filter change may never carry a page.
 *
 * `Omit<…, 'page'>` is the enforcement, and it is a type rather than a convention on purpose: this
 * is the classic bug in a filtered list — one `onChange` out of eight forgets to reset the page, and
 * the reader stays on page 11 of a result set that now has three. Here it is not possible to forget,
 * because there is no way to say it.
 *
 * It also replaces a server-side guarantee. The keyset cursor carried a fingerprint of the query
 * that minted it, so a mismatched filter was REJECTED with a 400 rather than silently answered. A
 * page number cannot be authenticated that way — an offset into a different result set is a
 * different page, not a corrupt one — so this type is now the only thing enforcing the rule.
 */
export type FilterPatch = Omit<Partial<SearchQuery>, 'page'>;

export interface SearchState {
  query: SearchQuery;
  /** Change a filter. ALWAYS returns to the first page. `replace` for continuous input (typing). */
  setFilter: (patch: FilterPatch, options?: { replace?: boolean }) => void;
  /** Turn to a page. Pushes, so one Back undoes one page turn. */
  goToPage: (page: number) => void;
  canGoPrevious: boolean;
}

export function useSearchState(basePath = '/'): SearchState {
  const { search, navigate } = useRoute();
  const query = useMemo(() => parseSearchQuery(search), [search]);

  const push = useCallback(
    (next: SearchQuery, options?: { replace?: boolean }) => {
      const params = toSearchParams(next).toString();
      navigate(`${basePath}${params ? `?${params}` : ''}`, options);
    },
    [basePath, navigate],
  );

  const setFilter = useCallback(
    (patch: FilterPatch, options?: { replace?: boolean }) => {
      push(normalise({ ...query, ...patch, page: 1 }), options);
    },
    [push, query],
  );

  /**
   * `goToPage` replaced a `goToCursor` + `goToPrevious` pair backed by a module-level
   * cursor → previous-cursor map. That map existed because a keyset cannot derive the page before
   * its anchor without running the query backwards, and it had to live at module scope because a
   * `useRef` died the moment the reader opened a posting — coming back with the browser's Back
   * button restored the right URL and then disabled "הקודם", since the remounted hook had no memory
   * of how it got there. A page number needs no memory: `page - 1` is the previous page on a fresh
   * load, on a shared link, and after any navigation at all.
   */
  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page === query.page) return;
      push({ ...query, page });
    },
    [push, query],
  );

  return { query, setFilter, goToPage, canGoPrevious: query.page > 1 };
}
